// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { LangLink as Link } from "@/components/LangLink";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import {
  Rocket,
  Globe,
  Info,
  BadgeHelp,
  CircuitBoard,
  GraduationCap,
  Building2,
} from "lucide-react";

// Images
import heroImage from "@/assets/hero-bizupod.jpg";
import cubesatsPng from "@/assets/bizupod.png";
import ctaImage from "@/assets/hero-contact.avif";
import bizupod2 from "@/assets/bizupod2.png";
import bizupod3 from "@/assets/bizupod3.png";
import bizupod4 from "@/assets/bizupod4.png";

export const Route = createFileRoute("/$lang/products/bizupod")({
  head: () => ({
    meta: [
      { title: i18n.t("products/bizupod:metaTitle", "BIZU POD CubeSat Deployer — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/bizupod:metaDesc",
          "BIZU POD is a family of CubeSat deployer systems developed in-house, ranging from 1U entry-level to 12U advanced platforms.",
        ),
      },
      { property: "og:title", content: i18n.t("products/bizupod:metaTitle", "BIZU POD CubeSat Deployer — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/bizupod:metaOgDesc",
          "Space-qualified, high-reliability CubeSat deployers with clean mechanical containment and electrical command releases.",
        ),
      },
      { property: "og:url", content: "/products/bizupod" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/bizupod" }],
  }),
  component: BIZUPodComponent,
});

function BIZUPodComponent() {
  const { t } = useTranslation("products/bizupod");

  const marketChallenges = [
    {
      title: t("challenges.accessTitle", "Access Barriers"),
      description: t("challenges.access",
        "Difficulty accessing foreign deployers (currency exchange, lead times, logistics and import constraints);"
      ),
      icon: Globe,
    },
    {
      title: t("challenges.gapTitle", "National Gap"),
      description: t("challenges.gap",
        "Absence of a national product with documentation aligned to launch vehicle requirements;"
      ),
      icon: Info,
    },
    {
      title: t("challenges.supportTitle", "Local Support"),
      description: t("challenges.support", "Need for technical support in Portuguese during integration and testing phases;"),
      icon: BadgeHelp,
    },
    {
      title: t("challenges.interfaceTitle", "Critical Interface"),
      description: t("challenges.interface",
        "Lack of clear standardization in the interface between deployer – launch vehicle – CubeSat."
      ),
      icon: CircuitBoard,
    },
  ];

  const targetAudience = [
    {
      title: t("audience.uniTitle", "Universities & Student Teams"),
      description: t("audience.uniDesc", "1U, 2U and 3U missions for academic and first-orbit projects."),
      icon: GraduationCap,
    },
    {
      title: t("audience.startupTitle", "Startups & Technology Companies"),
      description: t("audience.startupDesc", "In-orbit technology demonstrators and commercial CubeSat payloads."),
      icon: Rocket,
    },
    {
      title: t("audience.researchTitle", "Research Institutes & Government Agencies"),
      description: t("audience.researchDesc", "Institutional missions supporting national space capability development."),
      icon: Building2,
    },
  ];

  return (
    <PageShell>
      <PageHero
        title={<span className="keep-case">BIZU POD</span>}
        description={t("heroDesc", "BIZU POD is a family of CubeSats Deployer developed totally in-house. The first space-qualified was BIZU POD 1U.")}
        image={heroImage}
      />

      <ImageTextSection
        title={
          <>
            {t("whatTitle", "WHAT IS BIZU POD?")}
          </>
        }
        subtitle={t("whatSubtitle", "BIZU POD is a CubeSat deployer system that:")}
        image={cubesatsPng}
        imagePosition="right"
        variant="container"
        description={
          <>
            <ul className="space-y-4 ">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet1", "Accommodates the satellite during transportation, testing and integration;")}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet2", "Ensures proper mechanical containment throughout launch;")}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet3", "Releases the CubeSat in a controlled manner upon receiving the command from the launch vehicle.")}
                </span>
              </li>
            </ul>
            <p className="mt-5">
              {t("whatDesc1", "In practical terms, it serves as the “cradle” of your satellite — from the laboratory phase to the moment the spring deploys the CubeSat into space.")}
            </p>
            <p className="mt-5 font-bold">
              {t("whatDesc2", "The BIZU POD family was designed to support different mission profiles, offering versions such as:")}
            </p>

            {/* Mini specs box */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10 mt-4">
              <div>
                <div className="display text-xl text-white">
                  {t("whatSpec1Title", "BIZU POD 1U")}
                </div>
                <div className="text-xs text-white/50">
                  {t("whatSpec1Desc", "Ideal for entry-level missions, technology demonstrations and university projects;")}
                </div>
              </div>
              <div>
                <div className="display text-xl text-white">
                  {t("whatSpec2Title", "BIZU POD up to 12U")}
                </div>
                <div className="text-xs text-white/50">
                  {t("whatSpec2Desc", "Designed for more complex payloads, increased internal volume and missions with higher capacity requirements.")}
                </div>
              </div>
            </div>
          </>
        }
      />

      <section className="relative overflow-hidden bg-black py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 max-w-4xl mx-auto">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl text-center">
              {t("whyTitle", "Why did we create the BIZU POD?")}
            </h2>
            <p className="mt-6 text-lg text-center text-white/70 leading-relaxed">
              {t("whyDesc", "By supporting several CubeSat teams and working closely within the launch vehicle ecosystem, we identified recurring challenges:")}
            </p>
          </Reveal>
          <FeatureGrid columns={4} items={marketChallenges} />
        </div>
      </section>

      <section className="relative overflow-hidden bg-black pt-24 pb-4 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mx-auto max-w-4xl text-center">
            <h2 className="display text-3xl sm:text-4xl md:text-5xl leading-tight">
              {t("scaleTitle", "BIZU POD 1U, up to 12U: Scaling from Entry Missions to More Ambitious Projects")}
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
              {t("scaleDesc", "The BIZU POD architecture was designed to be scalable across different CubeSat sizes, while maintaining a consistent design philosophy, documentation framework and interface standards.")}
            </p>
          </Reveal>
        </div>
      </section>

      <ImageTextSection
        title={
          <>
            {t("pod1uTitle", "BIZU POD 1U")}
          </>
        }
        image={bizupod2}
        variant="container"
        description={
          <>
            <p className="font-bold mb-5">{t("pod1uDesigned", "Designed for:")}</p>

            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uD1", "First-orbit university missions;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uD2", "Compact technology demonstrators;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uD3", "Projects with cost constraints and tight schedules.")}
                </span>
              </li>
            </ul>

            <p className="mt-8 font-bold">{t("pod1uChar", "General Characteristics:")}</p>

            <ul className="mt-5 space-y-4">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uC1", "Compatible with the CubeSat 1U standard according to established industry specifications;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uC2", "Door-based containment system with mechanical locking and electrically actuated release mechanism;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uC3", "Internal spring sized to ensure controlled ejection within the exit velocity limits specified by the launch provider;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod1uC4", "High-strength machined metallic structure, with careful attention to tolerances and sliding surface finishing.")}
                </span>
              </li>
            </ul>
          </>
        }
      />
      <ImageTextSection
        title={
          <>
            {t("pod12uTitle", "BIZU POD up to 12U")}
          </>
        }
        image={bizupod3}
        imagePosition="right"
        variant="container"
        description={
          <>
            <p className="font-bold mb-5">{t("pod12uDesigned", "Designed for:")}</p>

            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uD1", "Missions with larger payloads;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uD2", "Greater capacity for power systems, antennas, cameras, and onboard subsystems;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uD3", "Projects evolving from 1U to more advanced platforms without changing the overall integration philosophy.")}
                </span>
              </li>
            </ul>

            <p className="mt-8 font-bold">
              {t("pod12uPrinciples", "Maintaining the same principles as the 1U version, with:")}
            </p>

            <ul className="mt-5 space-y-4">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uP1", "Internal geometry adapted for other formats like 2U, 3U, 6U and 12U formats, compliant with CubeSat standards;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uP2", "Release mechanism compatible with envelope and ejection velocity requirements;")}
                </span>
              </li>

              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("pod12uP3", "Mechanical and electrical interfaces aligned with launch vehicle and payload adapter requirements.")}
                </span>
              </li>
            </ul>
          </>
        }
      />
      <ImageTextSection
        title={t("approachTitle", "Engineering & Qualification Approach")}
        image={bizupod4}
        variant="background"
        description={t("approachDesc", "The BIZU POD development follows recognized industry references, including NASA GEVS (General Environmental Verification Standard) and applicable ECSS standards for structures, mechanisms and environmental requirements, supported by established mechanical, thermal and vacuum testing methodologies.")}
      />
      {/* WHO IS THE BIZU POD DESIGNED FOR? */}
      <section className="relative overflow-hidden bg-black py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 max-w-4xl mx-auto">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl text-center">
              {t("audienceTitle", "Who is the BIZU POD designed for?")}
            </h2>
          </Reveal>
          <FeatureGrid items={targetAudience} />
        </div>
      </section>
      <CtaSection
        image={ctaImage}
        title={t("ctaTitle", "Launch Your CubeSat with Confidence")}
        subtitle={t("ctaSubtitle", "Integrate your mission with the BIZU POD deployer – designed in Brazil, engineered for reliability and fully documented for seamless launch vehicle compatibility.")}
        buttonText={t("ctaBtn", "Submit order request")}
        href="/contact"
      />
    </PageShell>
  );
}

